import { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '../styled';

const TONE_BG = {
  mint: 'var(--qhs-st-normal-bg)',
  violet: 'var(--qhs-cat-wine-bg)',
  amber: 'var(--qhs-st-high-bg)',
  rose: 'var(--qhs-st-critical-bg)',
  indigo: 'var(--qhs-cat-indigo-bg)',
  sage: 'var(--qhs-cat-sage-bg)',
  stone: 'var(--qhs-cat-stone-bg)',
  default: 'var(--qhs-cat-indigo-bg)',
};

const TONE_FG = {
  mint: 'var(--qhs-st-normal-tx)',
  violet: 'var(--qhs-cat-wine-tx)',
  amber: 'var(--qhs-st-high-tx)',
  rose: 'var(--qhs-st-critical-tx)',
  indigo: 'var(--qhs-cat-indigo-tx)',
  sage: 'var(--qhs-cat-sage-tx)',
  stone: 'var(--qhs-cat-stone-tx)',
  default: 'var(--qhs-cat-indigo-tx)',
};

const Card = styled('button')({
  appearance: 'none',
  textAlign: 'left',
  display: 'grid',
  gridTemplateColumns: '36px 1fr 18px',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: 14,
  border: '1px solid var(--qhs-border)',
  borderRadius: 10,
  background: 'var(--qhs-surface)',
  cursor: 'pointer',
  transition: 'all 140ms ease',
  '&:hover': {
    borderColor: 'var(--qhs-brand)',
    background: 'var(--qhs-brand-tint-4)',
    transform: 'translateY(-1px)',
    boxShadow: 'var(--qhs-sh-2)',
  },
  '&.selected': {
    borderColor: 'var(--qhs-brand)',
    boxShadow: '0 0 0 3px var(--qhs-focus-ring)',
  },
});

const IconWrap = styled('span', { shouldForwardProp: p => p !== '$tone' })(({ $tone }) => ({
  width: 36,
  height: 36,
  borderRadius: 8,
  background: TONE_BG[$tone] || TONE_BG.default,
  color: TONE_FG[$tone] || TONE_FG.default,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { width: 18, height: 18 },
}));

const Body = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
});

const Title = styled('span')({
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--qhs-text-1)',
  lineHeight: 1.35,
  overflow: 'hidden',
  '& mark': {
    background: 'var(--qhs-brand-tint-14)',
    color: 'inherit',
    padding: '0 2px',
    borderRadius: 2,
  },
});

// Smooth expand/collapse via max-height transition. We trade the
// webkit-line-clamp ellipsis for animation-friendly max-height (the two
// don't compose well — line-clamp can't transition). The bottom mask
// keeps the truncation cue: text fades to transparent on the collapsed
// state's last line, signalling "there's more". 800px upper bound is
// well past any description we'd render (~44 lines at 12px/1.5).
const Desc = styled('span', { shouldForwardProp: p => p !== '$expanded' })(({ $expanded }) => ({
  fontSize: 12,
  color: 'var(--qhs-text-2)',
  lineHeight: 1.5,
  display: 'block',
  maxHeight: $expanded ? 800 : 36,
  overflow: 'hidden',
  transition: 'max-height 320ms cubic-bezier(0.4, 0, 0.2, 1)',
  WebkitMaskImage: $expanded ? 'none' : 'linear-gradient(to bottom, black 65%, transparent 100%)',
  maskImage: $expanded ? 'none' : 'linear-gradient(to bottom, black 65%, transparent 100%)',
  '& mark': { background: 'var(--qhs-brand-tint-14)', color: 'inherit', padding: '0 2px' },
}));

const ReadMoreBtn = styled('button')({
  alignSelf: 'flex-start',
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  color: 'var(--qhs-brand)',
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 0',
  cursor: 'pointer',
  marginTop: 2,
  '&:hover': { textDecoration: 'underline' },
});

const READ_MORE_THRESHOLD = 120;

const Meta = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 11,
  color: 'var(--qhs-text-3)',
  marginTop: 2,
});

const TAG_BG = {
  mint: 'var(--qhs-st-normal-bg)',
  violet: 'var(--qhs-cat-wine-bg)',
  amber: 'var(--qhs-st-high-bg)',
  rose: 'var(--qhs-st-critical-bg)',
  indigo: 'var(--qhs-cat-indigo-bg)',
  sage: 'var(--qhs-cat-sage-bg)',
  stone: 'var(--qhs-cat-stone-bg)',
  default: 'var(--qhs-cat-indigo-bg)',
};

const TAG_FG = {
  mint: 'var(--qhs-st-normal-tx)',
  violet: 'var(--qhs-cat-wine-tx)',
  amber: 'var(--qhs-st-high-tx)',
  rose: 'var(--qhs-st-critical-tx)',
  indigo: 'var(--qhs-cat-indigo-tx)',
  sage: 'var(--qhs-cat-sage-tx)',
  stone: 'var(--qhs-cat-stone-tx)',
  default: 'var(--qhs-cat-indigo-tx)',
};

const Tag = styled('span', { shouldForwardProp: p => p !== '$tone' })(({ $tone }) => ({
  display: 'inline-flex',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 600,
  background: TAG_BG[$tone] || TAG_BG.default,
  color: TAG_FG[$tone] || TAG_FG.default,
  whiteSpace: 'nowrap',
}));

const TagRow = styled('span')({
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: 4,
});

const Chevron = styled('span')({
  color: 'var(--qhs-text-3)',
  '& svg': { width: 14, height: 14 },
});

const ChevronSvg = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const DefaultIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// Replace each case-insensitive occurrence of `query` in `text` with
// <mark>match</mark>. Returns React-compatible array of nodes.
const highlight = (text, query) => {
  if (!text || !query) return text;
  const parts = String(text).split(
    new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig')
  );
  return parts.map((p, i) =>
    p.toLowerCase() === query.toLowerCase() ? <mark key={i}>{p}</mark> : <span key={i}>{p}</span>
  );
};

const CatalogItemCard = ({ item, query, tone = 'default', icon, tag, selected, onClick }) => {
  const { catalog } = useSelector(state => state.i18n);
  const [expanded, setExpanded] = useState(false);
  const showReadMore = (item.description ?? '').length > READ_MORE_THRESHOLD;

  const handleToggle = e => {
    e.stopPropagation();
    setExpanded(v => !v);
  };

  return (
    <Card type="button" className={selected ? 'selected' : ''} onClick={() => onClick(item)}>
      <IconWrap $tone={tone}>{icon || <DefaultIcon />}</IconWrap>
      <Body>
        <Title>{highlight(item.title, query)}</Title>
        {item.description && <Desc $expanded={expanded}>{highlight(item.description, query)}</Desc>}
        {showReadMore && (
          <ReadMoreBtn type="button" onClick={handleToggle}>
            {expanded ? catalog.readLess ?? 'Read less' : catalog.readMore}
          </ReadMoreBtn>
        )}
        {Array.isArray(tag) ? (
          <Meta>
            <TagRow>
              {tag.map(t => (
                <Tag key={t.id} $tone={t.tone}>
                  {t.label}
                </Tag>
              ))}
            </TagRow>
            {item.durationMin && <span>· {item.durationMin} min</span>}
          </Meta>
        ) : tag ? (
          <Meta>
            <Tag $tone={tone}>{tag}</Tag>
            {item.durationMin && <span>· {item.durationMin} min</span>}
          </Meta>
        ) : null}
      </Body>
      <Chevron>
        <ChevronSvg />
      </Chevron>
    </Card>
  );
};

export default CatalogItemCard;
